/* User-facing support page.
 *
 * Two views:
 *   /app/support           → list of MY tickets
 *   /app/support/:id       → single ticket thread + reply box
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LifeBuoy, ArrowLeft, Send, Loader2, MessageSquare,
  CheckCircle2, Clock, AlertCircle, ShieldCheck,
} from "lucide-react";

const STATUS_META = {
  open:          { label: "Open",          color: "bg-amber-100 text-amber-900 border-amber-200",   Icon: Clock },
  in_progress:   { label: "In Progress",   color: "bg-sky-100 text-sky-900 border-sky-200",         Icon: Loader2 },
  waiting_user:  { label: "Waiting on You", color: "bg-violet-100 text-violet-900 border-violet-200", Icon: AlertCircle },
  resolved:      { label: "Resolved",      color: "bg-emerald-100 text-emerald-900 border-emerald-200", Icon: CheckCircle2 },
  closed:        { label: "Closed",        color: "bg-stone-100 text-stone-700 border-stone-200",   Icon: CheckCircle2 },
};

const CATEGORY_LABEL = {
  bug: "Bug", billing: "Billing", feature_request: "Feature Request",
  onboarding: "Onboarding", other: "Other",
};


export default function Support() {
  const { ticketId } = useParams();
  if (ticketId) return <TicketThread ticketId={ticketId} />;
  return <TicketList />;
}


function TicketList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data?.items || []);
    } catch {
      toast.error("Couldn't load tickets.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="support-list-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs text-[var(--ink-2)] uppercase font-bold tracking-wider flex items-center gap-1">
            <LifeBuoy size={12} /> Support
          </div>
          <h1 className="font-display font-extrabold text-2xl mt-1">Your tickets</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Direct line to our support team. Replies usually land in &lt; 24h.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={18} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : tickets.length === 0 ? (
        <div className="card p-10 text-center" data-testid="support-empty">
          <ShieldCheck size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
          <div className="font-display font-bold text-lg">No tickets yet</div>
          <p className="text-sm text-[var(--ink-2)] mt-1 mb-4">
            Hit the Help button (bottom-right) any time to reach the team.
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-[var(--line)]">
          {tickets.map((t) => (
            <button
              key={t.id} type="button"
              onClick={() => nav(`/v3/support/${t.id}`)}
              className="w-full text-left p-4 hover:bg-stone-50 transition flex items-start gap-3"
              data-testid={`support-ticket-row-${t.id}`}
            >
              <StatusBadge status={t.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{t.subject}</span>
                  {t.unread_by_user && (
                    <span className="bg-rose-500 text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded">
                      New reply
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{t.ref}</span>
                  <span>·</span>
                  <span>{CATEGORY_LABEL[t.category] || t.category}</span>
                  <span>·</span>
                  <span>{t.message_count} {t.message_count === 1 ? "message" : "messages"}</span>
                  <span>·</span>
                  <span>{new Date(t.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function TicketThread({ ticketId }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setTicket(data?.ticket || null);
      setMessages(data?.messages || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Ticket not found.");
      } else {
        toast.error("Couldn't load ticket.");
      }
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${ticketId}/reply`, { body: reply });
      setReply("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send reply.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[var(--ink-2)]">
      <Loader2 size={18} className="animate-spin inline mr-2" /> Loading…
    </div>;
  }
  if (!ticket) {
    return (
      <div className="card p-8 text-center">
        <div className="font-display font-bold">Ticket not found</div>
        <Link to="/v3/support" className="text-emerald-600 text-sm font-semibold mt-2 inline-block">
          ← Back to all tickets
        </Link>
      </div>
    );
  }

  const isClosed = ticket.status === "closed";

  return (
    <div className="space-y-4" data-testid="support-thread-page">
      <Link to="/v3/support" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> All tickets
      </Link>

      <div className="card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-extrabold text-xl">{ticket.subject}</h1>
            <div className="text-xs text-[var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{ticket.ref}</span>
              <span>·</span>
              <span>{CATEGORY_LABEL[ticket.category] || ticket.category}</span>
              <span>·</span>
              <span>Opened {new Date(ticket.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <StatusBadge status={ticket.status} large />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-4">
          {messages.map((m) => (
            <MessageBubble key={m.id} msg={m} ticket={ticket} />
          ))}
        </div>

        {isClosed ? (
          <div className="border-t border-[var(--line)] p-4 bg-stone-50 text-center text-sm text-[var(--ink-2)]">
            This ticket is closed. <Link to="/v3/support" className="text-emerald-700 font-semibold">Open a new one →</Link>
          </div>
        ) : (
          <form onSubmit={send} className="border-t border-[var(--line)] p-4 bg-white">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              placeholder="Write a reply…"
              className="input w-full resize-none"
              maxLength={8000}
              data-testid="support-thread-reply"
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={sending || reply.trim().length < 1}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2"
                data-testid="support-thread-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


function MessageBubble({ msg, ticket }) {
  const isAdmin = msg.author_kind === "admin";
  const isMine = !isAdmin && msg.author_id === ticket.user_id;
  return (
    <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] ${isAdmin ? "" : "items-end"}`}>
        <div className={`text-[11px] mb-1 ${isAdmin ? "text-emerald-700 font-bold" : "text-[var(--ink-3)] text-right"}`}>
          {isAdmin ? "iSpeedToClose Support" : (isMine ? "You" : msg.author_name)}
          <span className="font-normal text-[var(--ink-3)] ml-1.5">
            {new Date(msg.created_at).toLocaleString()}
          </span>
        </div>
        <div className={`rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isAdmin
            ? "bg-emerald-50 border border-emerald-200 text-[var(--ink)]"
            : "bg-[var(--ink)] text-white"
        }`}>
          {msg.body}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ status, large = false }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${meta.color} ${
      large ? "text-xs font-bold" : "text-[10px] font-bold uppercase tracking-wide"
    }`}>
      <meta.Icon size={large ? 12 : 10} className={status === "in_progress" ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
}

export { STATUS_META, CATEGORY_LABEL };
